import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
   fetchGroups,
   addGroup,
   updateGroup,
   removeGroup,
} from "../../store/groupsSlice";
import {
   assignGroupsToManager,
   getGroupById,
   getGroups,
   getManagerGroups,
   removeManagerFromGroup,
} from "../../api/groupsService";
import { getUserById } from "../../api/usersService";
import { ROLES, normalizeRole } from "../../auth/access";
import { UserContext } from "../../UserContext";
import { closePopup } from "../Common/popupUIStore";
import useDistricts from "../../hooks/useDistricts";
import { districtOptions } from "../../utils/districts";
import ConfirmDeleteButton from "../Common/ConfirmDeleteButton";
import StudentItem from "../Common/StudentItem";
import {
   getAvatarColorFromName,
   getInitials,
} from "./StudentInfo/studentInfoShared";
import {
   readGroupManagerId as readManagerIdFromGroup,
   readGroupProfessorId as readProfessorIdFromGroup,
   readGroupSector,
   readGroupToken,
} from "../../utils/groupFields";

const toNum = (v) =>
   v === null || v === undefined || v === "" || Number.isNaN(Number(v))
      ? undefined
      : Number(v);

function errorMessage(err, fallback = "Nu s-au putut salva modificările.") {
   if (err == null) return fallback;
   if (typeof err === "string") {
      const t = err.trim();
      return t || fallback;
   }
   const t = String(err.message || err.payload || "").trim();
   return t || fallback;
}

const isProtectedToken = (token) =>
   String(token ?? "")
      .trim()
      .toUpperCase() === "ABCD1234";

function findUserById(users, id) {
   const s = String(id ?? "");
   if (!s) return null;
   return (
      users.find((x) =>
         [x?.id, x?.userId, x?.user?.id].some(
            (k) => k != null && String(k) === s,
         ),
      ) || null
   );
}

const fullName = (p) => `${p?.firstName || ""} ${p?.lastName || ""}`.trim();

/* Managerul curent al grupei — citit defensiv din obiectul grupă. */
function readGroupManagerId(group) {
   const n = readManagerIdFromGroup(group);
   return n ? String(n) : "";
}

function readGroupProfessorId(group) {
   const n = readProfessorIdFromGroup(group);
   return n ? String(n) : "";
}

function entityId(value) {
   const n = Number(
      value?.id ?? value?.groupId ?? value?.group?.id ?? value?.data?.id ?? value,
   );
   return Number.isInteger(n) && n > 0 ? n : undefined;
}

async function resolveCreatedGroupId(created, nameHint) {
   const direct = entityId(created);
   if (direct) return direct;
   try {
      const list = await getGroups();
      const wanted = String(nameHint || "").trim();
      const match = (list || []).find(
         (g) => String(g?.name || "").trim() === wanted,
      );
      return entityId(match);
   } catch {
      return undefined;
   }
}

export default function GroupEditPopup({
   groupId = null,
   mode = "edit",
   onClose,
}) {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext);
   const userRole = normalizeRole(user?.role);
   const isAdmin = userRole === ROLES.ADMIN;
   const canEditManager = isAdmin;
   const canDeleteGroup = isAdmin;
   const canCreateGroup = isAdmin;

   const groupsState = useSelector((state) => state.groups || {});
   const usersState = useSelector((state) => state.users || {});
   const groups = Array.isArray(groupsState.list) ? groupsState.list : [];
   const users = useMemo(() => {
      const merged = new Map();
      const fromGroups = Array.isArray(groupsState.users)
         ? groupsState.users
         : [];
      const fromUsers = Array.isArray(usersState.list) ? usersState.list : [];
      for (const u of [...fromGroups, ...fromUsers]) {
         const id = u?.id ?? u?.userId ?? u?.user?.id;
         if (id == null) continue;
         merged.set(String(id), u?.user && u.user.id != null ? u.user : u);
      }
      return Array.from(merged.values());
   }, [groupsState.users, usersState.list]);

   const isCreate = mode === "create" || groupId == null;
   const group = useMemo(
      () =>
         isCreate
            ? null
            : groups.find((g) => String(g.id) === String(groupId)) || null,
      [groups, groupId, isCreate],
   );

   const [name, setName] = useState("");
   const [token, setToken] = useState("");
   const [sector, setSector] = useState("");
   const districts = useDistricts();
   // sectoarele active + cel al grupei, dacă e unul vechi
   const sectorOptions = districtOptions(districts, sector);
   const [professorId, setProfessorId] = useState("");
   const [managerId, setManagerId] = useState("");
   const [baselineManagerId, setBaselineManagerId] = useState("");
   const managerTouchedRef = useRef(false);
   const [view, setView] = useState("form");
   const [pickQuery, setPickQuery] = useState("");
   const [extraPeople, setExtraPeople] = useState([]);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState("");
   const [confirmDelete, setConfirmDelete] = useState(false);

   const people = useMemo(() => {
      const merged = new Map();
      for (const u of [...users, ...extraPeople]) {
         const id = u?.id ?? u?.userId ?? u?.user?.id;
         if (id == null) continue;
         merged.set(String(id), u?.user && u.user.id != null ? u.user : u);
      }
      return Array.from(merged.values());
   }, [users, extraPeople]);

   const professors = useMemo(() => {
      const list = people.filter((u) => normalizeRole(u?.role) === "PROFESSOR");
      const selected = findUserById(people, professorId);
      if (
         selected &&
         !list.some((p) => String(p.id) === String(selected.id))
      ) {
         return [selected, ...list];
      }
      return list;
   }, [people, professorId]);
   const managers = useMemo(
      () => people.filter((u) => normalizeRole(u?.role) === "MANAGER"),
      [people],
   );

   // Init when opening create vs a specific group — not on every list refetch.
   useEffect(() => {
      managerTouchedRef.current = false;
      if (isCreate) {
         setName("");
         setToken("");
         setSector("");
         setProfessorId("");
         setManagerId("");
         setBaselineManagerId("");
         return;
      }
      setName(group?.name || "");
      setToken(readGroupToken(group));
      setSector(readGroupSector(group));
      setProfessorId(readGroupProfessorId(group));
      const mid = readGroupManagerId(group);
      setManagerId(mid);
      setBaselineManagerId(mid);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- only remount/open
   }, [group?.id, isCreate]);

   useEffect(() => {
      if (isCreate || !group?.id) return undefined;
      let cancelled = false;
      (async () => {
         try {
            const detail = await getGroupById(group.id);
            if (cancelled || !detail) return;
            const mid = readGroupManagerId(detail);
            if (mid) {
               setBaselineManagerId(mid);
               if (!managerTouchedRef.current) setManagerId(mid);
            }
            const pid = readGroupProfessorId(detail);
            if (pid) setProfessorId(String(pid));
            const sec = readGroupSector(detail);
            if (sec) setSector(sec);
            const tok = readGroupToken(detail);
            if (tok) setToken(tok);
         } catch {
            // lista rămâne fallback
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [isCreate, group?.id]);

   // Reverse-lookup: manager + professorId vin pe rândul de atribuire.
   useEffect(() => {
      if (isCreate || !group?.id) return undefined;
      if (managerId && professorId) return undefined;
      if (!managers.length) return undefined;

      let cancelled = false;
      (async () => {
         for (const m of managers) {
            try {
               const arr = await getManagerGroups(m.id);
               const match = arr.find(
                  (g) => String(g?.id ?? g?.groupId) === String(group.id),
               );
               if (match) {
                  if (cancelled) return;
                  const mid = String(match.managerId ?? m.id);
                  setBaselineManagerId(mid);
                  if (!managerTouchedRef.current && !managerId) {
                     setManagerId(mid);
                  }
                  const pid = readGroupProfessorId(match);
                  if (pid && !professorId) setProfessorId(String(pid));
                  return;
               }
            } catch {
               // ignorăm managerii ale căror grupe nu pot fi citite
            }
            if (cancelled) return;
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [isCreate, group?.id, managerId, professorId, managers]);

   const selectedProfessor = useMemo(
      () => findUserById(people, professorId),
      [people, professorId],
   );
   const selectedManager = useMemo(
      () => findUserById(people, managerId),
      [people, managerId],
   );

   useEffect(() => {
      const pid = toNum(professorId);
      if (!pid || findUserById(people, pid)) return undefined;
      let cancelled = false;
      (async () => {
         try {
            const person = await getUserById(pid);
            const raw = person?.data && person.data.id != null ? person.data : person;
            if (!cancelled && raw?.id != null) {
               setExtraPeople((prev) =>
                  prev.some((x) => String(x.id) === String(raw.id))
                     ? prev
                     : [...prev, raw],
               );
            }
         } catch {
            // numele rămâne gol până există userul în listă
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [professorId, people]);

   const close = () => {
      if (typeof onClose === "function") onClose();
      else closePopup();
   };

   const openPicker = (which) => {
      if (which === "manager" && !canEditManager) return;
      setPickQuery("");
      setView(which);
   };

   const syncManagerAssignment = async (gid) => {
      const nextId = toNum(managerId);
      const prevId = toNum(baselineManagerId);
      if (String(nextId || "") === String(prevId || "")) return;

      if (prevId) {
         try {
            await removeManagerFromGroup(prevId, gid);
         } catch {
            // continuăm chiar dacă scoaterea vechiului manager eșuează
         }
      }
      if (nextId) {
         await assignGroupsToManager(nextId, [gid]);
      }
   };

   const handleSave = async () => {
      if (isCreate && !canCreateGroup) {
         setError("Nu ai permisiune să creezi grupe.");
         return;
      }
      const trimmed = String(name || "").trim();
      if (!trimmed) {
         setError("Completează numele grupei.");
         return;
      }
      if (!toNum(professorId)) {
         setError("Selectează un profesor.");
         return;
      }
      if (canEditManager && !toNum(managerId)) {
         setError("Selectează un manager.");
         return;
      }

      setSaving(true);
      setError("");
      try {
         const pid = toNum(professorId);
         const mid = canEditManager ? toNum(managerId) : undefined;
         const payload = {
            name: trimmed,
            professorId: pid,
         };
         const tokenVal = String(token || "").trim();
         if (tokenVal) payload.token = tokenVal;
         const sectorVal = String(sector || "").trim();
         if (sectorVal) payload.sector = sectorVal;

         if (isCreate) {
            const created = await dispatch(addGroup(payload)).unwrap();
            const newId = await resolveCreatedGroupId(created, trimmed);
            if (!newId) {
               throw new Error(
                  "Grupa a fost creată, dar nu s-a putut atribui managerul.",
               );
            }
            await assignGroupsToManager(mid, [newId]);
         } else {
            await dispatch(
               updateGroup({
                  id: group.id,
                  ...payload,
               }),
            ).unwrap();
            if (canEditManager) {
               await syncManagerAssignment(group.id);
            }
         }

         try {
            await dispatch(fetchGroups()).unwrap();
         } catch {
            // salvarea a reușit; reîncărcarea listei nu trebuie să arate eroare de salvare
         }
         close();
      } catch (e) {
         setError(errorMessage(e));
      } finally {
         setSaving(false);
      }
   };

   const handleDelete = async () => {
      if (!canDeleteGroup) return;
      if (isCreate || !group) return;
      if (isProtectedToken(group.token)) {
         setConfirmDelete(false);
         return;
      }
      setSaving(true);
      setError("");
      try {
         await dispatch(removeGroup(group.id)).unwrap();
         await dispatch(fetchGroups());
         close();
      } catch (e) {
         setError(e?.message || "Nu s-a putut șterge grupa.");
         setSaving(false);
         setConfirmDelete(false);
      }
   };

   const protectedGroup = !isCreate && isProtectedToken(group?.token);

   /* ===================== Picker sub-view ===================== */
   if (view === "professor" || (view === "manager" && canEditManager)) {
      const isManager = view === "manager";
      const selectedId = isManager ? managerId : professorId;
      const people = isManager ? managers : professors;
      const roleLabel = isManager ? "Manager" : "Profesor";

      const q = String(pickQuery || "").trim().toLowerCase();
      const list = q
         ? people.filter((p) =>
              [fullName(p), p?.phone, p?.email]
                 .filter(Boolean)
                 .join(" ")
                 .toLowerCase()
                 .includes(q),
           )
         : people;

      const applyPick = (id) => {
         if (isManager) {
            managerTouchedRef.current = true;
            setManagerId(id);
         } else {
            setProfessorId(id);
         }
         setView("form");
      };

      return (
         <div className="createReservationPopup groupEditPopupUI">
            <div className="createReservationPopup__header">
               <h2 className="createReservationPopup__title">
                  {isManager ? "Alege manager" : "Alege profesor"}
               </h2>
            </div>

            <div className="createReservationPopup__content">
               <div className="createReservationPopup__search-header">
                  <input
                     type="text"
                     className="createReservationPopup__search-input"
                     placeholder={
                        isManager
                           ? "Caută manager după nume sau telefon..."
                           : "Caută profesor după nume sau telefon..."
                     }
                     value={pickQuery}
                     onChange={(e) => setPickQuery(e.target.value)}
                     autoComplete="off"
                  />
                  <div className="createReservationPopup__search-header-actions">
                     <button
                        type="button"
                        className="createReservationPopup__btn createReservationPopup__btn--normal"
                        onClick={() => setView("form")}
                     >
                        Înapoi
                     </button>
                  </div>
               </div>

               <div className="groupEditPopupUI__pickerList">
                  {list.length === 0 ? (
                     <p className="groupEditPopupUI__pickerEmpty">
                        Niciun rezultat
                     </p>
                  ) : (
                     list.map((p) => {
                        const active = String(p.id) === String(selectedId);
                        const secondaryText =
                           [p?.phone, p?.email]
                              .filter(Boolean)
                              .join(" • ") || roleLabel;
                        return (
                           <StudentItem
                              key={p.id}
                              student={p}
                              color={getAvatarColorFromName(p)}
                              initials={getInitials(p)}
                              secondaryText={secondaryText}
                              highlightQuery={pickQuery}
                              onOpen={() => applyPick(String(p.id))}
                              className={
                                 active
                                    ? "groupEditPopupUI__pickerItem groupEditPopupUI__pickerItem--active"
                                    : "groupEditPopupUI__pickerItem"
                              }
                           />
                        );
                     })
                  )}
               </div>
            </div>
         </div>
      );
   }

   /* ===================== Main form ===================== */
   return (
      <div className="createReservationPopup groupEditPopupUI">
         <div className="createReservationPopup__header">
            <h2 className="createReservationPopup__title">
               {isCreate ? "Grupă nouă" : "Editează grupa"}
            </h2>
         </div>

         <div className="createReservationPopup__content">
            <div className="createReservationPopup__group-body">
               <label className="createReservationPopup__field">
                  <span className="createReservationPopup__field-label">
                     Nume grupă
                  </span>
                  <input
                     className="createReservationPopup__input"
                     placeholder="Numele grupei"
                     value={name}
                     onChange={(e) => setName(e.target.value)}
                  />
               </label>

               <label className="createReservationPopup__field">
                  <span className="createReservationPopup__field-label">
                     Token / Key
                  </span>
                  <input
                     className="createReservationPopup__input"
                     placeholder="Token / Key (opțional)"
                     value={token}
                     onChange={(e) => setToken(e.target.value)}
                  />
               </label>

               <div className="createReservationPopup__field">
                  <span className="createReservationPopup__field-label">
                     Sector
                  </span>
                  <div className="groupEditPopupUI__sectorGrid">
                     {sectorOptions.map((option) => {
                        const isActive = sector === option;
                        return (
                           <button
                              key={option}
                              type="button"
                              className={`groupEditPopupUI__sectorButton ${
                                 isActive ? "is-active" : ""
                              }`}
                              aria-pressed={isActive ? "true" : "false"}
                              onClick={() => setSector(option)}
                           >
                              {option}
                           </button>
                        );
                     })}
                  </div>
               </div>
            </div>

            <div className="createReservationPopup__group-body">
               <div className="createReservationPopup__form-row createReservationPopup__form-row--spaced">
                  <div
                     className="createReservationPopup__field createReservationPopup__field--clickable createReservationPopup__field--grow-1"
                     onClick={() => openPicker("professor")}
                  >
                     <span className="createReservationPopup__field-label">
                        Profesor
                     </span>
                     <div className="createReservationPopup__field-line">
                        <span className="createReservationPopup__field-text">
                           {selectedProfessor
                              ? fullName(selectedProfessor)
                              : "Selectează profesor"}
                        </span>
                     </div>
                     <div className="createReservationPopup__field-line">
                        <span className="createReservationPopup__field-text">
                           {selectedProfessor?.phone || "Telefon profesor"}
                        </span>
                     </div>
                  </div>

                  <div
                     className={
                        canEditManager
                           ? "createReservationPopup__field createReservationPopup__field--clickable createReservationPopup__field--grow-1"
                           : "createReservationPopup__field createReservationPopup__field--grow-1"
                     }
                     onClick={
                        canEditManager ? () => openPicker("manager") : undefined
                     }
                  >
                     <span className="createReservationPopup__field-label">
                        Manager
                        {!canEditManager ? (
                           <span className="groupEditPopupUI__fieldHint">
                              {" "}
                              (doar admin)
                           </span>
                        ) : null}
                     </span>
                     <div className="createReservationPopup__field-line">
                        <span className="createReservationPopup__field-text">
                           {selectedManager
                              ? fullName(selectedManager)
                              : canEditManager
                                ? "Selectează manager"
                                : "—"}
                        </span>
                     </div>
                     <div className="createReservationPopup__field-line">
                        <span className="createReservationPopup__field-text">
                           {selectedManager?.phone || "Telefon manager"}
                        </span>
                     </div>
                  </div>
               </div>
            </div>

            {error ? (
               <div className="createReservationPopup__field createReservationPopup__field--error groupEditPopupUI__error">
                  <span className="createReservationPopup__field-text">
                     {error}
                  </span>
               </div>
            ) : null}

            <div
               className={[
                  "createReservationPopup__actions-row",
                  !isCreate && !protectedGroup && canDeleteGroup
                     ? "createReservationPopup__actions-row--two"
                     : "createReservationPopup__actions-row--single",
               ].join(" ")}
            >
               {!isCreate && !protectedGroup && canDeleteGroup ? (
                  <ConfirmDeleteButton
                     confirming={confirmDelete}
                     onStart={() => setConfirmDelete(true)}
                     onCancel={() => setConfirmDelete(false)}
                     onConfirm={handleDelete}
                     disabled={saving}
                  />
               ) : null}

               <button
                  type="button"
                  className="createReservationPopup__btn createReservationPopup__btn--save createReservationPopup__btn--action"
                  onClick={handleSave}
                  disabled={saving}
               >
                  {saving
                     ? "Se salvează..."
                     : isCreate
                       ? "Creează grupa"
                       : "Salvează"}
               </button>
            </div>
         </div>
      </div>
   );
}
